// @ts-nocheck
import { LessonPlan, TeachingModel, Quiz, Question, QuestionType, Difficulty, Presentation, Homework, HomeworkTaskType, Game, GradeLevel, GameType, SlideLayoutType, Slide, DocumentSegment, QuizSystem, QuizCategory, QuizFormat, Activity, ActivityType, ChildLearningPlan, PlanDifficulty, PlanDuration, DailyPlan, HistoryStory, LiteratureAnalysisGuide, ScienceExplanation, VideoResource, MatrixRow, SpecificationTopic, COGNITIVE_LEVELS, PresentationTheme, GenerateVideosOperation, Banner } from '../types';

/** Theme có thể là enum (chuỗi) hoặc object từ AI — dùng cho prompt ảnh, tránh "[object Object]". */
function presentationThemeToPromptLabel(theme: Presentation['theme'] | undefined): string {
    if (theme == null) return PresentationTheme.ELEGANT;
    if (typeof theme === 'string') return theme;
    if (typeof theme === 'object' && theme !== null && 'mood' in theme && typeof (theme as { mood?: string }).mood === 'string') {
        const o = theme as { mood: string; fontStyle?: string };
        return o.fontStyle ? `${o.mood} (${o.fontStyle})` : o.mood;
    }
    return PresentationTheme.ELEGANT;
}
import { APP_MODE, API_CONFIG, API_BASE_URL, API_EDU_PREFIX, resolvePublicFileUrl, AIProvider } from "../config";

export { API_EDU_PREFIX };
import { saveUserDataToLocal, updateLocalResource } from '../utils/storageUtils';

/**
 * Mock Type for legacy schemas if any still exist
 */
const Type = {
    OBJECT: 'object',
    ARRAY: 'array',
    STRING: 'string',
    NUMBER: 'number',
    BOOLEAN: 'boolean'
};

// =================================================================================
// IMPORTANT ARCHITECTURAL NOTE
// =================================================================================
// All AI generation and data operations must go through the backend SaaS API.
// Direct calls to AI models from the frontend are disabled for security and consistency.
// =================================================================================

// Simple token cache for API calls
let EDUAPP_ACCESS_TOKEN: string | null = null;
let CURRENT_USER_ID: string | null = null;
let EDUAPP_AUTH_TYPE: 'standard' | 'student_code' | null = null;

export const setEduappAccessToken = (token: string | null, userId: string | null = null, authType: 'standard' | 'student_code' | null = null) => { 
    EDUAPP_ACCESS_TOKEN = token; 
    CURRENT_USER_ID = userId ?? null;
    EDUAPP_AUTH_TYPE = authType ?? (token ? 'standard' : null);
    persistAuth(); 
};

export const getEduappAuthType = (): 'standard' | 'student_code' | null => EDUAPP_AUTH_TYPE;
export const EDUAPP_CREDIT_BALANCE_UPDATED_EVENT = 'eduapp-credit-balance-updated';

const emitCreditBalanceUpdated = (balance: number, source: string) => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(EDUAPP_CREDIT_BALANCE_UPDATED_EVENT, {
        detail: { balance, source }
    }));
};

const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        const payload = await response.json().catch(() => ({}));
        const message = payload?.error || payload?.message;
        return typeof message === 'string' && message.trim() ? message : fallback;
    }

    const text = await response.text().catch(() => '');
    const compact = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return compact ? compact.slice(0, 180) : fallback;
};

export const refreshCreditBalance = async (source: string): Promise<number | null> => {
    if (APP_MODE !== 'PRODUCTION' || !EDUAPP_ACCESS_TOKEN || EDUAPP_AUTH_TYPE === 'student_code') {
        return null;
    }
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/credits/balance`, { method: 'GET' });
        if (!res.ok) return null;
        const data = await res.json().catch(() => ({}));
        const balance = Number((data as { balance?: number }).balance);
        if (Number.isFinite(balance)) {
            emitCreditBalanceUpdated(balance, source);
            return balance;
        }
    } catch (error) {
        console.warn('Unable to refresh credit balance:', error);
    }
    return null;
};

/** Tải ảnh/video đính kèm cho bài nộp nhiệm vụ. Trả về { url, type, name }. */
export const uploadSubmissionAttachment = async (file: File): Promise<{ url: string; type: string; name: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(apiUrl(`${API_EDU_PREFIX}/submissions/upload-attachment`), {
        method: 'POST',
        headers: { 'Accept': 'application/json', ...authHeader() } as Record<string, string>,
        body: formData,
        credentials: 'include',
    });
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    return { url: formatAssetUrl(data.url) || data.url, type: data.type || 'image', name: data.name || file.name };
};

/** Tải tài liệu thư viện lên (PDF, Word, TXT...). */
export const uploadLibraryDocument = async (file: File, onProgress?: (pct: number) => void): Promise<any> => {
    const res = await uploadFileWithProgress(`${API_EDU_PREFIX}/library/upload-document`, file, onProgress);
    return res;
};

/** Tải hình ảnh thư viện lên. */
export const uploadLibraryImage = async (file: File, onProgress?: (pct: number) => void): Promise<any> => {
    const res = await uploadFileWithProgress(`${API_EDU_PREFIX}/library/upload-image`, file, onProgress);
    return res;
};

/** Tải video thư viện lên. */
export const uploadLibraryVideo = async (file: File, onProgress?: (pct: number) => void): Promise<any> => {
    const res = await uploadFileWithProgress(`${API_EDU_PREFIX}/library/upload-video`, file, onProgress);
    return res;
};

/** Tải audio thư viện lên (sử dụng chung route video của backend). */
export const uploadLibraryAudio = async (file: File, onProgress?: (pct: number) => void): Promise<any> => {
    const res = await uploadFileWithProgress(`${API_EDU_PREFIX}/library/upload-video`, file, onProgress);
    return res;
};

/** Helper to upload file with progress using XMLHttpRequest */
const uploadFileWithProgress = (path: string, file: File, onProgress?: (pct: number) => void): Promise<any> => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append('file', file);

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                const pct = Math.round((e.loaded * 100) / e.total);
                onProgress(pct);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText));
                } catch {
                    resolve(xhr.responseText);
                }
            } else {
                let errorMsg = 'Lỗi tải lên';
                try {
                    const errorData = JSON.parse(xhr.responseText);
                    errorMsg = errorData.message || errorMsg;
                } catch {}
                reject(new Error(errorMsg));
            }
        });

        xhr.addEventListener('error', () => reject(new Error('Lỗi kết nối mạng')));
        xhr.addEventListener('abort', () => reject(new Error('Tải lên bị hủy')));

        xhr.open('POST', apiUrl(path));
        
        // Add headers
        xhr.setRequestHeader('Accept', 'application/json');
        const auth = authHeader() as Record<string, string>;
        if (auth['Authorization']) {
            xhr.setRequestHeader('Authorization', auth['Authorization']);
        }
        
        xhr.withCredentials = true;
        xhr.send(formData);
    });
};

/** Tổng hợp nhiều tài liệu thư viện thành một. */
export const combineLibraryDocuments = async (ids: string[], name: string): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/library/combine-documents`, {
        method: 'POST',
        body: JSON.stringify({ ids, name }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Lỗi tổng hợp' }));
        throw new Error(err.message || 'Lỗi tổng hợp');
    }
    return await res.json();
};

/** Nộp bài / xác nhận hoàn thành (hỗ trợ cả parent và student_code). Trả về { id } khi thành công. */
export const submitStudentWork = async (assignmentId: string, studentId: string, submittedDate: string, content?: { answers?: Record<string, any>; attachments?: { url: string; type: string; name: string }[] }): Promise<{ id?: string | number } | null> => {
    if (APP_MODE !== 'PRODUCTION' && !EDUAPP_ACCESS_TOKEN) return null;
    const isStudentCode = EDUAPP_AUTH_TYPE === 'student_code';
    const payload: Record<string, any> = { assignment_id: assignmentId, student_id: String(studentId), status: 'da-nop', submitted_date: submittedDate };
    if (content && (content.attachments?.length || Object.keys(content.answers || {}).length)) {
        payload.content = content;
    }
    const path = isStudentCode ? `${API_EDU_PREFIX}/student/submit` : `${API_EDU_PREFIX}/submissions`;
    const res = await apiFetch(path, { method: 'POST', body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    return data?.id != null ? { id: data.id } : data;
};

/** Lấy chi tiết một bài tập/nhiệm vụ (hỗ trợ cả student_code). */
export const fetchAssignmentById = async (id: string | number): Promise<any | null> => {
    if (APP_MODE !== 'PRODUCTION') return null;
    try {
        const isStudentCode = EDUAPP_AUTH_TYPE === 'student_code';
        const path = isStudentCode ? `${API_EDU_PREFIX}/student/assignments/${id}` : `${API_EDU_PREFIX}/assignments/${id}`;
        const response = await apiFetch(path);
        if (!response.ok) return null;
        return await response.json();
    } catch (error) {
        console.error(`Error fetching assignment with id ${id}:`, error);
        return null;
    }
};

/** Chấm bài tự động bằng AI (Giáo viên). Trả về gợi ý điểm và nhận xét. */
export const aiGradeSubmissionApi = async (submissionId: string | number): Promise<{ 
    suggestedGrade: number; 
    teacherComments: string; 
    taskResults?: Record<string, { isCorrect: boolean; feedback: string }>;
    creditsDeducted?: number;
}> => {
    if (APP_MODE !== 'PRODUCTION') {
        return { 
            suggestedGrade: 8.5, 
            teacherComments: "[MOCK AI] Bài làm khá tốt, nội dung mạch lạc và bám sát yêu cầu. Tuy nhiên cần chú ý hơn về cách diễn đạt ở câu cuối.",
            creditsDeducted: 10
        };
    }
    const res = await apiFetch(`${API_EDU_PREFIX}/submissions/${submissionId}/ai-grade`, { method: 'POST' });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Lỗi chấm bài AI' }));
        throw new Error(err.error || 'Lỗi chấm bài AI');
    }
    const data = await res.json();
    await refreshCreditBalance('ai-grade');
    return data;
};

/** Đăng xuất tất cả thiết bị (student code login). */
export const studentLogoutAll = async (): Promise<void> => {
  if (APP_MODE !== 'PRODUCTION' || EDUAPP_AUTH_TYPE !== 'student_code') return;
  await apiFetch(`${API_EDU_PREFIX}/student/logout-all`, { method: 'POST' });
};

const AUTH_STORAGE_KEY = 'EDUAPP_AUTH';

const loadAuthFromStorage = () => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    EDUAPP_ACCESS_TOKEN = parsed.accessToken || null;
    EDUAPP_AUTH_TYPE = parsed.authType === 'student_code' ? 'student_code' : (parsed.accessToken ? 'standard' : null);
    // legacy fields ignored: provider, providerId, providerToken
  } catch {}
};

const persistAuth = () => {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ 
      accessToken: EDUAPP_ACCESS_TOKEN, 
      authType: EDUAPP_AUTH_TYPE 
    }));
  } catch {}
};

loadAuthFromStorage();

const authHeader = () => EDUAPP_ACCESS_TOKEN ? { 'Authorization': `Bearer ${EDUAPP_ACCESS_TOKEN}` } : {};

// Centralized fetch wrapper for backend API
const apiUrl = (path: string) => `${API_BASE_URL}${path}`;
const tryRefreshToken = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${API_BASE_URL}${API_EDU_PREFIX}/auth/refresh`, { 
        method: 'POST', 
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.access_token) {
      setEduappAccessToken(data.access_token);
      return true;
    }
  } catch {}
  return false;
};

export const apiFetch = async (path: string, init?: RequestInit) => {
    const baseHeaders = { 
        'Content-Type': 'application/json', 
        'Accept': 'application/json',
        ...(init?.headers || {})
    };
    const headers = { ...baseHeaders, ...authHeader() } as Record<string, string>;
    
    const url = apiUrl(path);
    const res = await fetch(url, { ...init, headers, credentials: 'include' });
    if (res.status !== 401) return res;
    // Student code login không có refresh token
    if (EDUAPP_AUTH_TYPE === 'student_code') {
        window.dispatchEvent(new CustomEvent('eduapp-logout', { detail: { reason: 'session_expired' } }));
        return res;
    }
    // Attempt refresh once
    const refreshed = await tryRefreshToken();
    if (!refreshed) {
        window.dispatchEvent(new CustomEvent('eduapp-logout', { detail: { reason: 'session_expired' } }));
        return res;
    }
    const retryHeaders = { ...baseHeaders, ...authHeader() } as Record<string, string>;
    const retryRes = await fetch(apiUrl(path), { ...init, headers: retryHeaders, credentials: 'include' });
    if (retryRes.status === 401) {
        window.dispatchEvent(new CustomEvent('eduapp-logout', { detail: { reason: 'session_expired' } }));
    }
    return retryRes;
};

// Join Requests
export const getClassUpdates = async (classId: string | number): Promise<{ class: any; joinRequests: any[]; students: any[] } | null> => {
    if (APP_MODE !== 'PRODUCTION') return null;
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/updates`);
        if (!res.ok) return null;
        const data = await res.json();
        return data;
    } catch { return null; }
};

export const getJoinRequests = async (): Promise<any[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/join-requests`);
        if (!res.ok) return [];
        const data = await res.json();
        return data.requests || [];
    } catch { return []; }
};

/** Kiểm tra sớm: con đã trong lớp / có yêu cầu chờ chưa (trước khi hiện form xác nhận) */
export const checkJoinEligibility = async (classId: string | number, childId: string | number, studentId?: string | number): Promise<{ canJoin: boolean; message?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { canJoin: true };
    try {
        const params = new URLSearchParams({ class_id: String(classId), child_id: String(childId) });
        if (studentId) params.set('student_id', String(studentId));
        const res = await apiFetch(`${API_EDU_PREFIX}/join-requests/check?${params}`);
        const data = await res.json();
        return { canJoin: !!data.can_join, message: data.message };
    } catch { return { canJoin: true }; }
};

export const createJoinRequest = async (payload: {
    child_id?: string|number;
    class_id: string|number;
    student_id?: string|number;
    message?: string;
    is_student_self_join?: boolean;
    parent_info?: { name?: string; phone?: string; relationship?: string; email?: string };
    student_info?: { name?: string; real_name?: string; dob?: string; gender?: string };
}): Promise<{ success: boolean; isDuplicate?: boolean; message?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { success: true };
    const isStudentCode = EDUAPP_AUTH_TYPE === 'student_code';
    if (isStudentCode) {
        return {
            success: false,
            message: 'Học sinh đăng nhập bằng mã được gán sẵn vào lớp. Chức năng tham gia lớp khác không áp dụng.',
        };
    }
    const path = `${API_EDU_PREFIX}/join-requests`;
    const body: Record<string, unknown> = { ...payload };
    if (payload.is_student_self_join) {
        body.is_student_self_join = 1;
        delete body.child_id;
    }
    if (payload.parent_info) body.parent_info = payload.parent_info;
    if (payload.student_info) body.student_info = payload.student_info;
    const res = await apiFetch(path, {
        method: 'POST',
        body: JSON.stringify(body)
    });
    if (res.ok) return { success: true };
    const bodyText = await res.text();
    let parsed: { message?: string; code?: string } = {};
    try {
        parsed = bodyText ? JSON.parse(bodyText) : {};
    } catch { /* ignore */ }
    const msg = (parsed.message || parsed.error || '').toString().toLowerCase();
    const fullText = bodyText.toLowerCase();
    const code = parsed.code || '';
    // Đã trong lớp hoặc đang chờ: trả về message thân thiện, coi như không lỗi nghiêm trọng
    if (res.status === 422) {
        if (code === 'already_in_class' || code === 'pending_exists') {
            return { success: true, isDuplicate: true, message: parsed.message };
        }
        const isDuplicateMessage = msg.includes('đã được gửi') || msg.includes('đang chờ') ||
            msg.includes('thành viên') || fullText.includes('đã được gửi') || fullText.includes('đang chờ');
        if (isDuplicateMessage) {
            return { success: true, isDuplicate: true, message: parsed.message };
        }
    }
    return { success: false, message: parsed.message };
};

export const approveJoinRequestApi = async (id: string|number): Promise<boolean> => {
    if (APP_MODE !== 'PRODUCTION') return true;
    const res = await apiFetch(`${API_EDU_PREFIX}/join-requests/${id}/approve`, { method: 'POST' });
    return res.ok;
};

export const rejectJoinRequestApi = async (id: string|number, rejectReason?: string): Promise<boolean> => {
    if (APP_MODE !== 'PRODUCTION') return true;
    const res = await apiFetch(`${API_EDU_PREFIX}/join-requests/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reject_reason: rejectReason || '' }),
    });
    return res.ok;
};

/** Hủy liên kết tài khoản phụ huynh với học sinh (GV thực hiện) */
export const unlinkParentFromStudentApi = async (classId: string|number, studentId: string|number, parentUserId: number, reason?: string): Promise<{ success: boolean; message?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { success: true };
    const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/${studentId}/unlink-parent`, {
        method: 'POST',
        body: JSON.stringify({ parent_user_id: parentUserId, reason }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, message: (data as any).error || 'Không thể hủy liên kết' };
    return { success: true };
};

/** Hủy liên kết tài khoản học sinh với hồ sơ (GV thực hiện) */
export const unlinkStudentAccountApi = async (classId: string|number, studentId: string|number, reason?: string): Promise<{ success: boolean; message?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { success: true };
    const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/${studentId}/unlink-student-account`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, message: (data as any).error || 'Không thể hủy liên kết' };
    return { success: true };
};

/**
 * Tải tệp PDF từ Backend để đảm bảo chất lượng và font Tiếng Việt
 */
export const downloadPdfFromBackend = async (type: 'lesson-plan' | 'quiz' | 'homework' | 'activity', id: string | number, filename: string) => {
    if (id.toString().startsWith('id_')) {
        throw new Error('Vui lòng lưu tài liệu trước khi tải PDF.');
    }
    const path = `${API_EDU_PREFIX}/export/pdf/${type}/${id}?_t=${Date.now()}`;
    /** UI thường fallback sang PDF phía trình duyệt — không log error ồn ào khi chỉ cần thử máy chủ trước */
    return downloadFromApi(path, filename, { silent: true });
};

/**
 * DOCX từ backend: LaTeX → KaTeX HTML → DOCX (hiện hỗ trợ quiz).
 */
export const downloadDocxFromBackend = async (
    type: 'quiz',
    id: string | number,
    filename: string,
    options?: { variant?: 'student' | 'answers' | 'full' },
) => {
    if (id.toString().startsWith('id_')) {
        throw new Error('Vui lòng lưu tài liệu trước khi tải DOCX.');
    }
    const variant = options?.variant ?? 'student';
    const path = `${API_EDU_PREFIX}/export/docx/${type}/${id}?variant=${encodeURIComponent(variant)}`;
    const name = filename.endsWith('.docx') ? filename : `${filename}.docx`;
    return downloadFromApi(path, name, { silent: true });
};

/**
 * Tải tệp từ API (hỗ trợ xác thực)
 */
export const downloadFromApi = async (
    path: string,
    filename: string,
    options?: { silent?: boolean }
) => {
    try {
        const res = await apiFetch(path);
        if (!res.ok) {
            let detail = `HTTP ${res.status}`;
            const ct = res.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
                const j = await res.json().catch(() => ({}));
                if (j && typeof j === 'object' && (j as any).error) detail = String((j as any).error);
            }
            throw new Error(detail === `HTTP ${res.status}` ? 'Không thể tải tệp' : `Không thể tải tệp: ${detail}`);
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        if (!options?.silent) console.error('Download error:', error);
        throw error;
    }
};

/**
 * Ensures a minimum delay for an async operation, useful for showing ads
 * on loading screens for at least a certain amount of time.
 */
export const withMinimumDelay = async <T>(promise: Promise<T>, minDelayMs: number = 6000, shouldDelay: boolean = true): Promise<T> => {
    if (!shouldDelay) return await promise;
    const start = Date.now();
    try {
        const result = await promise;
        const elapsed = Date.now() - start;
        if (elapsed < minDelayMs) {
            await new Promise(resolve => setTimeout(resolve, minDelayMs - elapsed));
        }
        return result;
    } catch (error) {
        const elapsed = Date.now() - start;
        if (elapsed < minDelayMs) {
            await new Promise(resolve => setTimeout(resolve, minDelayMs - elapsed));
        }
        throw error;
    }
};

/**
 * Handles errors from the Gemini API and returns a user-friendly error object.
 */
const handleGeminiError = (error: unknown, context: string): Error => {
    console.error(`Lỗi trong khi ${context}:`, error);

    let userFriendlyMessage = `Đã xảy ra lỗi không xác định trong khi ${context}. Vui lòng thử lại.`;
    let errorMessage = '';

    if (error instanceof Error) {
        errorMessage = error.message;
    } else if (typeof error === 'string') {
        errorMessage = error;
    } else {
        try {
            // Attempt to stringify if it's an object, which might be the case for API error responses
            errorMessage = JSON.stringify(error);
        } catch {
            errorMessage = 'Lỗi không thể chuyển thành chuỗi.';
        }
    }
    
    // Convert to lower case for easier matching
    const lowerErrorMessage = errorMessage.toLowerCase();

    // Check for common Gemini API error patterns
    if (lowerErrorMessage.includes('resource_exhausted') || lowerErrorMessage.includes('429')) {
        userFriendlyMessage = 'Bạn đã vượt quá hạn ngạch sử dụng API cho tính năng này. Vui lòng kiểm tra gói cước của bạn hoặc thử lại sau.';
        (error as any).isRetryable = true;
        (error as any).errorType = 'RATE_LIMIT';
    } else if (lowerErrorMessage.includes('api key not valid') || (lowerErrorMessage.includes('[400') && lowerErrorMessage.includes('api_key'))) {
        userFriendlyMessage = 'Khóa API không hợp lệ. Vui lòng kiểm tra lại cấu hình và đảm bảo khóa API của bạn đúng.';
        (error as any).isRetryable = false;
        (error as any).errorType = 'CONFIG_ERROR';
    } else if (lowerErrorMessage.includes('safety') || lowerErrorMessage.includes('blocked')) {
        userFriendlyMessage = 'Yêu cầu của bạn đã bị chặn vì lý do an toàn (chứa nội dung không phù hợp). Vui lòng điều chỉnh lại kịch bản.';
        (error as any).isRetryable = false;
        (error as any).errorType = 'SAFETY';
    } else if (lowerErrorMessage.includes('failed to parse json') || lowerErrorMessage.includes('invalid json')) {
        userFriendlyMessage = 'AI đã trả về dữ liệu có định dạng không hợp lệ. Vui lòng thử lại.';
        (error as any).isRetryable = true;
        (error as any).errorType = 'INVALID_RESPONSE';
    } else if (lowerErrorMessage.includes('deadline exceeded') || lowerErrorMessage.includes('504') || lowerErrorMessage.includes('timeout') || lowerErrorMessage.includes('failed to fetch')) {
        userFriendlyMessage = 'Yêu cầu đã hết thời gian chờ hoặc lỗi kết nối. Máy chủ có thể đang bận. Vui lòng thử lại.';
        (error as any).isRetryable = true;
        (error as any).errorType = 'TIMEOUT';
    } else if (lowerErrorMessage.includes('[500')) {
        userFriendlyMessage = 'Đã có lỗi xảy ra từ phía máy chủ AI. Vui lòng thử lại sau.';
        (error as any).isRetryable = true;
        (error as any).errorType = 'SERVER_ERROR';
    }

    const finalError = new Error(userFriendlyMessage);
    (finalError as any).isRetryable = (error as any).isRetryable ?? false;
    (finalError as any).errorType = (error as any).errorType ?? 'UNKNOWN';
    return finalError;
};


// --- API Functions ---

export const segmentDocument = async (content: string, documentName: string, documentId?: string): Promise<DocumentSegment[]> => {
    // Mục đích: Gửi nội dung tài liệu thô lên backend để xử lý phân đoạn.
    // Request Payload Example:
    // POST /api/v1/generate/segments
    // BODY: {
    //   "content": "Nội dung tài liệu dài...",
    //   "documentName": "Tên tài liệu.docx",
    //   "documentId": "123"
    // }
    
    // Success Response Example (200 OK):
    // {
    //   "segments": [ { "title": "Chương 1", "content": "Nội dung chương 1..." }, ... ]
    // }
    const payload = { content, documentName, documentId };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/segments`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi phân tích tài liệu.');
        }
        const data = await response.json();
        await refreshCreditBalance('segments');
        return data.segments;
    } catch (error) {
        throw handleGeminiError(error, "phân tích tài liệu từ máy chủ");
    }
};

export const generateLessonPlan = async (
    gradeLevel: GradeLevel,
    subject: string,
    topic: string,
    detailedInstruction: string,
    contextContent: string | null,
    model: TeachingModel,
    teachingDuration: number,
    allowAiAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraOptions?: { classLevel?: string; textbookSet?: string; selectedDigitalCompetencies?: string[] }
): Promise<LessonPlan> => {
    const payload = { gradeLevel, subject, topic, detailedInstruction, contextContent, model, teachingDuration, allowAiAugmentation, aiProvider, id, ...extraOptions };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/lesson-plan`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi tạo giáo án.');
        }
        const result = await response.json();
        await refreshCreditBalance('lesson-plan');
        return { ...result, gradeLevel, model, teachingDuration, ...extraOptions };
    } catch (error) {
        throw handleGeminiError(error, "tạo giáo án từ máy chủ");
    }
};

export interface GeneratedImageResult {
    imageUrl: string;
    imageId?: string | number;
    image?: any;
}

/**
 * Tham chiếu resource cha (giáo án, bài tập...) khi tạo ảnh từ trong trang đó.
 * Backend lưu vào meta giao dịch credit để TransactionHistory hiển thị "thuộc bài X"
 * và cho phép click sang xem chính bài đó.
 */
export interface ImageGenerationContext {
    resource_type: 'lesson_plan' | 'slides' | 'presentation' | 'homework' | 'quiz' | 'game' | 'activity' | 'oral_quiz';
    resource_id: string | number;
    resource_title?: string;
}

export const generateImage = async (prompt: string, context?: ImageGenerationContext): Promise<GeneratedImageResult> => {
    // Luôn gọi xuống backend để tạo hình ảnh, lưu trữ và quản lý credit
    const payload: { prompt: string; context?: ImageGenerationContext } = { prompt };
    if (context && context.resource_type && (context.resource_id || context.resource_id === 0)) {
        payload.context = context;
    }
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/image`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi tạo hình ảnh.');
        }
        const data = await response.json();
        await refreshCreditBalance('image');
        return {
            imageUrl: data.imageUrl,
            imageId: data.imageId ?? data.image?.id,
            image: data.image,
        };
    } catch (error) {
        throw handleGeminiError(error, "tạo hình ảnh từ máy chủ");
    }
};

export const generateImageForSlide = async (
    slide: Slide,
    presentationTheme?: Presentation['theme'],
    presentationRef?: { id?: string | number; title?: string }
): Promise<{ newPrompt?: string, imageUrl: string, imageId?: string | number }> => {
    const themeStyle = presentationThemeToPromptLabel(presentationTheme);
    
    // FIX: The original prompt was too long, causing a token limit error.
    // This now uses the dedicated `imagePrompt` from the slide content, which is concise and designed for image generation.
    // A simple fallback is created if `imagePrompt` is missing.
    const c = slide.content;
    const slideContent = c.explanation || c.activityDescription || c.question || c.discussionQuestion || 
                        (c.mainPoints ? c.mainPoints.join(', ') : '') || 
                        (c.summaryPoints ? c.summaryPoints.join(', ') : '');
    const finalPrompt = c.imagePrompt || `Một bức hình minh họa kỹ thuật số sạch sẽ, hiện đại, mang tính giáo dục theo chủ đề "${themeStyle}" về "${slide.title}". ${slideContent ? `Ngữ cảnh: ${slideContent}` : ''}.`;
    
    // Safeguard against excessively long prompts
    if (finalPrompt.length > 4000) {
        throw new Error("Image prompt exceeds the safe length limit.");
    }
    
    const ctx: ImageGenerationContext | undefined = presentationRef?.id && !String(presentationRef.id).startsWith('id_')
        ? { resource_type: 'slides', resource_id: presentationRef.id, resource_title: presentationRef.title }
        : undefined;
    const generated = await generateImage(finalPrompt, ctx);
    return { imageUrl: generated.imageUrl, imageId: generated.imageId };
};

export const generateSlides = async (
    topic: string,
    detailedInstruction: string,
    /** Chuỗi tự do khi phụ huynh chọn «không gắn cấp học» — khớp prompt backend. */
    educationalLevel: GradeLevel | string,
    contextContent: string | null,
    generationOptions: { mode: 'auto', duration: number } | { mode: 'custom', count: number },
    includeIntro: boolean,
    includeSummary: boolean,
    allowAiAugmentation: boolean,
    sourceLessonPlan?: LessonPlan,
    aiProvider: AIProvider = 'gemini',
    id?: string | number, // Thêm id để hỗ trợ cập nhật khi tạo lại
    designMode: 'basic' | 'professional' = 'professional',
    /** Gợi ý vai trò người tạo + mục đích (phụ huynh / học sinh) — backend đưa vào prompt. */
    audienceContext?: string | null,
    extraOptions?: { classLevel?: string; textbookSet?: string }
): Promise<Presentation> => {
    const payload = {
        topic, detailedInstruction, educationalLevel, contextContent, generationOptions, includeIntro, includeSummary, allowAiAugmentation, sourceLessonPlan, aiProvider, id, designMode,
        ...(audienceContext != null && String(audienceContext).trim() !== '' ? { audienceContext: String(audienceContext).trim() } : {}),
        ...extraOptions
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/slides`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi tạo trình chiếu.');
        }
        const data = await response.json();
        await refreshCreditBalance('slides');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo trình chiếu từ máy chủ");
    }
};

/** Nâng cấp trình chiếu lên HTML5 chuyên nghiệp */
export const upgradePresentationToHtml5 = async (
    id: string | number,
    force: boolean = false,
    aiProvider: AIProvider = 'gemini'
): Promise<{ html_url: string, share_token?: string, public_html5_url?: string | null }> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/slides/upgrade-html5`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ id, force, aiProvider })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi nâng cấp trình chiếu.');
        }
        const data = await response.json();
        await refreshCreditBalance('slides');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "nâng cấp trình chiếu HTML5 từ máy chủ");
    }
};

/** Xuất trình chiếu chuyên nghiệp thành file PPTX qua backend */
export const exportSlidesToPptx = async (presentation: Presentation): Promise<Blob> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/slides/export-pptx`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ presentation })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi xuất PPTX.');
        return await response.blob();
    } catch (error) {
        throw handleGeminiError(error, "xuất file PowerPoint từ máy chủ");
    }
};

export const generateHomework = async (
    topic: string,
    detailedInstruction: string,
    contextContent: string | null,
    allowAiAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    config?: {
        format?: string;
        mcqCount?: number;
        essayCount?: number;
        difficultyLevel?: string;
        matrixConfig?: any;
    },
    id?: string | number,
    extraOptions?: { gradeLevel?: GradeLevel; subject?: string; classLevel?: string; textbookSet?: string }
): Promise<Homework> => {
    const payload = { topic, detailedInstruction, contextContent, allowAiAugmentation, aiProvider, ...config, id, ...extraOptions };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/homework`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo bài tập.');
        const data = await response.json();
        await refreshCreditBalance('homework');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo bài tập từ máy chủ");
    }
};

/** Phân tích ảnh đề bài / trang bài tập trong thư viện để gợi ý chủ đề, format và bối cảnh (vision). */
export const analyzeHomeworkImageFromLibrary = async (
    imageId: string | number
): Promise<{
    topic: string;
    detailed_description: string;
    /** Dàn ý từng phần/câu trong ảnh (đánh số) */
    item_by_item_outline: string;
    /** Câu hỏi mẫu diễn đạt lại (mỗi dòng một stem) */
    paraphrased_stems: string;
    layout_summary: string;
    suggested_homework_format: string;
    language_detected: string;
    generation_instructions: string;
}> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/homework-image-meta`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ image_id: imageId }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || 'Không thể phân tích ảnh.');
        }
        const json = await response.json();
        const d = json.data || json;
        return {
            topic: String(d.topic || ''),
            detailed_description: String(d.detailed_description || ''),
            item_by_item_outline: String(d.item_by_item_outline || ''),
            paraphrased_stems: String(d.paraphrased_stems || ''),
            layout_summary: String(d.layout_summary || ''),
            suggested_homework_format: String(d.suggested_homework_format || 'auto'),
            language_detected: String(d.language_detected || ''),
            generation_instructions: String(d.generation_instructions || ''),
        };
    } catch (error) {
        throw handleGeminiError(error, 'phân tích ảnh bài tập');
    }
};

export const generateActivity = async (
  gradeLevel: GradeLevel,
  activityType: ActivityType,
  objective: string,
  keywords: string,
  contextContent: string | null,
  allowAiAugmentation: boolean,
  aiProvider: AIProvider = 'gemini',
  id?: string | number,
  extraOptions?: { subject?: string; classLevel?: string; textbookSet?: string }
): Promise<Activity> => {
    const payload = { gradeLevel, activityType, objective, keywords, contextContent, allowAiAugmentation, aiProvider, id, ...extraOptions };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/activity`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo hoạt động.');
        const data = await response.json();
        await refreshCreditBalance('activity');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo hoạt động từ máy chủ");
    }
};

/**
 * AI tạo nội dung SKKN theo từng module.
 * Backend: POST /api/edu/generate/skkn
 * @param module - 'part1'|'part2'|'part3'|'part4'|'part5'|'part6'|'part7'
 * @param context - Dữ liệu thu thập + SKKN hiện tại
 * @param qualityLevel - 'school'|'district'|'province'
 */
export const generateSKKNSection = async (
    module: string,
    context: Record<string, any>,
    qualityLevel: 'school' | 'district' | 'province' = 'school'
): Promise<Record<string, any>> => {
    const payload = { module, context, qualityLevel };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/skkn`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.message || 'Lỗi từ máy chủ khi tạo SKKN.');
        }
        const data = await response.json();
        await refreshCreditBalance('skkn');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo nội dung SKKN từ máy chủ");
    }
};

export interface MusicGenerationOptions {
    lyrics: string;
    title?: string;
    mode?: 'quick' | 'enterprise';
    duration?: number; // seconds, up to 180 for Pro
    vocal_style?: 'realistic' | 'instrumental' | 'expressive';
    tempo?: number; // BPM
    language?: string;
    genre?: string;
    image_reference?: string; // base64 or URL
    lyrics_alignment?: boolean;
}

export const generateMusic = async (options: string | MusicGenerationOptions, title?: string, mode: 'quick' | 'enterprise' = 'quick'): Promise<any> => {
    let payload: any;
    if (typeof options === 'string') {
        payload = { lyrics: options, title, mode };
    } else {
        payload = { ...options };
        if (!payload.mode) payload.mode = mode;
    }
    
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/music`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Lỗi từ máy chủ khi tạo nhạc.');
        }
        const data = await response.json();
        await refreshCreditBalance('music');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo nhạc từ máy chủ");
    }
};

export const fetchMusicStatus = async (generationId: string): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/music-status?generation_id=${encodeURIComponent(generationId)}`, {
            method: 'GET',
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Lỗi từ máy chủ khi kiểm tra trạng thái tạo nhạc.');
        }
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, "kiểm tra trạng thái tạo nhạc từ máy chủ");
    }
};

export const generateStudyAssistant = async (messages: { role: 'user' | 'ai', text: string }[]): Promise<{ reply: string }> => {
    try {
        const path = EDUAPP_AUTH_TYPE === 'student_code'
            ? `${API_EDU_PREFIX}/student/study-assistant`
            : `${API_EDU_PREFIX}/generate/study-assistant`;
        const response = await apiFetch(path, {
            method: 'POST',
            body: JSON.stringify({ messages })
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error((err as { message?: string }).message || 'Không thể kết nối Trợ lý học tập.');
        }
        const data = await response.json();
        await refreshCreditBalance('study-assistant');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "Trợ lý học tập");
    }
};

export const generateEnglishChat = async (messages: { role: 'user' | 'ai', text: string }[], level?: string): Promise<any> => {
    const payload = { messages, level };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/english-chat`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi trò chuyện tiếng Anh.');
        const data = await response.json();
        await refreshCreditBalance('english-chat');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "trò chuyện tiếng Anh từ máy chủ");
    }
};

export const generateSmartDesign = async (template: string, prompt: string, title?: string, referenceImage?: string, contentFile?: string, contentUrl?: string, contentText?: string): Promise<any> => {
    const payload = { template, prompt, title, referenceImage, contentFile, contentUrl, contentText };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/smart-design`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi thiết kế thông minh.');
        const data = await response.json();
        await refreshCreditBalance('smart-design');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "thiết kế thông minh từ máy chủ");
    }
};

export const enhanceImage = async (image: string, prompt: string): Promise<string> => {
    const payload = { image, prompt };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/image-enhance`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi nâng cấp hình ảnh.');
        const data = await response.json();
        await refreshCreditBalance('image-enhance');
        return data.imageUrl;
    } catch (error) {
        throw handleGeminiError(error, "nâng cấp hình ảnh từ máy chủ");
    }
};

export const formatAssetUrl = (url?: string): string => {
    return resolvePublicFileUrl(url);
};

export const initializeVideoProject = async (script: string): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video-project`, {
            method: 'POST',
            body: JSON.stringify({ script })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi khởi tạo dự án video.');
        const data = await response.json();
        await refreshCreditBalance('video-project-init');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "khởi tạo dự án video");
    }
};

export const generateCreativeVideo = async (scenes: any[], characters: any[], script?: string, projectId?: number, force: boolean = false): Promise<any> => {
    const payload = { scenes, characters, script, projectId, isSceneBased: true, force };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo video.');
        const data = await response.json();
        await refreshCreditBalance('video-project');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo video từ máy chủ");
    }
};

export const generateSingleScene = async (projectId: number | string, sceneId: string, force: boolean = false): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video-projects/${projectId}/generate-scene`, {
            method: 'POST',
            body: JSON.stringify({ sceneId, force })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo cảnh lẻ.');
        const data = await response.json();
        await refreshCreditBalance('video-scene');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo cảnh video đơn lẻ");
    }
};

export const fetchSceneResult = async (projectId: number | string, sceneId: string): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video-projects/${projectId}/fetch-scene-result`, {
            method: 'POST',
            body: JSON.stringify({ sceneId })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi lấy kết quả cảnh.');
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, "lấy kết quả cảnh video");
    }
};

export const mergeVideoProject = async (projectId: number | string): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video-projects/${projectId}/merge`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi ghép video.');
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, "ghép các cảnh video");
    }
};

export const uploadCharacterImage = async (file: File): Promise<{ imageUrl: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
        const response = await fetch(apiUrl(`${API_EDU_PREFIX}/generate/upload-character`), {
            method: 'POST',
            headers: {
                ...authHeader(),
                'Accept': 'application/json',
            },
            body: formData,
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tải ảnh lên.');
        const res = await response.json();
        return res;
    } catch (error) {
        throw handleGeminiError(error, "tải ảnh nhân vật lên");
    }
};

/**
 * Tải một ảnh lên server và trả về URL.
 */
export const uploadImageResource = async (file: File): Promise<string> => {
    const res = await uploadCharacterImage(file);
    return res.imageUrl;
};

export const generateGameFrameworkContent = async (
    gradeLevel: GradeLevel,
    gameType: GameType,
    topic: string,
    detailedInstruction: string,
    contextContent: string | null,
    allowAiAugmentation: boolean,
    sourceLessonPlan?: LessonPlan | null,
  settings?: Record<string, any>,
  aiProvider: AIProvider = 'gemini',
  id?: string | number,
  extraOptions?: { subject?: string; classLevel?: string; textbookSet?: string }
): Promise<Game> => {
    const payload = { gradeLevel, gameType, topic, detailedInstruction, contextContent, allowAiAugmentation, sourceLessonPlan, settings, aiProvider, id, ...extraOptions };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/game-content`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo game.');
        const jsonResponse = await response.json();
        await refreshCreditBalance('game');
        // Flatten if nested under 'data'
        const result = jsonResponse?.data && (jsonResponse.data.content || jsonResponse.data.title) ? { ...jsonResponse, ...jsonResponse.data } : jsonResponse;
        
        // Ensure title exists and metadata is merged
        return { 
            ...result, 
            title: result.title || topic || gameType || 'Trò chơi mới',
            gradeLevel, 
            gameType, 
            topic 
        };
    } catch (error) {
        throw handleGeminiError(error, "tạo game từ máy chủ");
    }
};

export const generateChildLearningPlan = async (
    age: number,
    gender: 'male' | 'female' | 'other',
    goal: string,
    difficulty: PlanDifficulty,
    duration: PlanDuration,
    fileContext: string | null
): Promise<Pick<ChildLearningPlan, 'title' | 'dailyPlans'>> => {
    const payload = { age, gender, goal, difficulty, duration, fileContext };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/child-plan`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo lộ trình học tập.');
        const data = await response.json();
        await refreshCreditBalance('child-plan');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo lộ trình học tập từ máy chủ");
    }
};

export const generateHistoryStory = async (event: string, perspective: string, style: string, age: string): Promise<HistoryStory> => {
    const payload = { event, perspective, style, age };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/history-story`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo truyện lịch sử.');
        const data = await response.json();
        await refreshCreditBalance('history-story');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo truyện lịch sử từ máy chủ");
    }
};

export const generateLiteratureAnalysis = async (inputText: string): Promise<LiteratureAnalysisGuide> => {
    const payload = { inputText };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/literature-analysis`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo gợi ý văn học.');
        const data = await response.json();
        await refreshCreditBalance('literature-analysis');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo gợi ý văn học từ máy chủ");
    }
};

export const generateScienceExplanation = async (concept: string, age: string): Promise<ScienceExplanation> => {
    const payload = { concept, age };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/science-explanation`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi giải thích khoa học.');
        const data = await response.json();
        await refreshCreditBalance('science-explanation');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "giải thích khoa học từ máy chủ");
    }
};

export const generateSampleData = async (title: string, subject: string, gradeLevel: GradeLevel): Promise<{ text: string }> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/sample-data`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ title, subject, gradeLevel })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo dữ liệu mẫu.');
        const data = await response.json();
        await refreshCreditBalance('sample-data');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo dữ liệu mẫu từ máy chủ");
    }
};

export const generateTestMatrix = async (
    gradeLevel: GradeLevel, 
    subject: string, 
    topics: string[], 
    duration: number, 
    category: QuizCategory, 
    contextContent: string | null, 
    allowAiAugmentation: boolean, 
    constraints?: any,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<{ matrix: MatrixRow[] }> => {
    const payload = { 
        gradeLevel, subject, topics, duration, category, contextContent, 
        allowAiAugmentation, constraints, aiProvider, id,
        ...extraData 
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/test-matrix`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo ma trận.');
        const data = await response.json();
        await refreshCreditBalance('test-matrix');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo ma trận từ máy chủ"); }
};

export const generateTestSpecification = async (
    gradeLevel: GradeLevel, 
    subject: string, 
    matrix: MatrixRow[], 
    contextContent: string | null, 
    allowAiAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<{ specification: SpecificationTopic[] }> => {
    const payload = { 
        gradeLevel, subject, matrix, contextContent, allowAiAugmentation, 
        aiProvider, id,
        ...extraData 
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/test-specification`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo bản đặc tả.');
        const data = await response.json();
        await refreshCreditBalance('test-specification');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo bản đặc tả từ máy chủ"); }
};

export const generateQuizFromMatrix = async (
    gradeLevel: GradeLevel, 
    subject: string, 
    title: string, 
    matrix: MatrixRow[], 
    specification: SpecificationTopic[], 
    duration: number, 
    contextContent: string | null, 
    allowAiAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<Quiz> => {
    const payload = { 
        gradeLevel, subject, title, matrix, specification, duration, 
        contextContent, allowAiAugmentation, aiProvider, id,
        ...extraData 
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/quiz-from-matrix`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo đề.');
        const data = await response.json();
        await refreshCreditBalance('quiz-from-matrix');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo đề từ máy chủ"); }
};

export const generateSimpleQuiz = async (
    gradeLevel: GradeLevel, 
    subject: string, 
    title: string, 
    scope: string, 
    duration: number, 
    context: string | null, 
    allowAugmentation: boolean, 
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<Quiz> => {
    const payload = { 
        gradeLevel, subject, title, scope, duration, context, 
        allowAugmentation, aiProvider, id,
        ...extraData 
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/simple-quiz`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo đề nhanh.');
        const data = await response.json();
        await refreshCreditBalance('simple-quiz');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo đề nhanh từ máy chủ"); }
};

export const generateQuizForModel = async (
    model: TeachingModel, 
    config: any, 
    gradeLevel: GradeLevel, 
    subject: string, 
    title: string, 
    scope: string, 
    duration: number, 
    context: string | null, 
    allowAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<Quiz> => {
    const payload = { 
        model, config, gradeLevel, subject, title, scope, duration, 
        context, allowAugmentation, aiProvider, id,
        ...extraData 
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/model-quiz`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo bài đánh giá.');
        const data = await response.json();
        await refreshCreditBalance('model-quiz');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo bài đánh giá từ máy chủ"); }
};

export const generateVideo = async (prompt: string): Promise<GenerateVideosOperation> => {
    const payload = { prompt };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (response.status !== 202) throw new Error('Lỗi từ máy chủ khi khởi tạo video.');
        const data = await response.json();
        await refreshCreditBalance('video');
        return data; 
    } catch (error) {
        throw handleGeminiError(error, "khởi tạo video từ máy chủ");
    }
};

/**
 * Proxies multiple image URLs through the backend to get their base64 representation in batch.
 * Useful for bypassing CORS when generating PPTX/PDF.
 */
export const fetchImagesAsBase64Batch = async (imageUrls: string[]): Promise<Record<string, string>> => {
    if (imageUrls.length === 0) return {};

    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/proxy-base64-batch`, {
            method: 'POST',
            body: JSON.stringify({ urls: imageUrls })
        });
        if (!response.ok) return imageUrls.reduce((acc, url) => ({ ...acc, [url]: url }), {});
        const data = await response.json();
        return data.results || {};
    } catch (error) {
        console.error("Error fetching base64 proxy batch:", error);
        return imageUrls.reduce((acc, url) => ({ ...acc, [url]: url }), {});
    }
};

/**
 * Single version of the proxy helper.
 */
export const fetchImageAsBase64 = async (imageUrl: string): Promise<string> => {
    const results = await fetchImagesAsBase64Batch([imageUrl]);
    return results[imageUrl] || imageUrl;
};

export const getVideoOperation = async (operationName: string): Promise<GenerateVideosOperation> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/operations/video/${operationName.split('/').pop()}`, {
            method: 'GET',
            headers: {},
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi kiểm tra tiến trình video.');
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, "kiểm tra tiến trình video từ máy chủ");
    }
};

export const saveVideoProject = async (project: Omit<VideoProject, 'id' | 'createdAt'>): Promise<VideoProject> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/video-projects`, {
        method: 'POST',
        body: JSON.stringify({
            name: project.name,
            source_activity_id: project.sourceActivityId,
            scenes: project.scenes,
            characters: project.characters
        })
    });
    if (!res.ok) throw new Error('Không lưu được dự án video');
    const data = await res.json();
    return data.data || data;
};

export const updateVideoProject = async (project: VideoProject): Promise<void> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/video-projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
            name: project.name,
            source_activity_id: project.sourceActivityId,
            scenes: project.scenes,
            characters: project.characters
        })
    });
    if (!res.ok) throw new Error('Không cập nhật được dự án video');
};

export const generateInteractiveVideoScript = async (videoUrl: string, prompt: string, context?: any): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/interactive-video`, {
        method: 'POST',
        body: JSON.stringify({ videoUrl, prompt, context })
    });
    if (!res.ok) throw new Error('Lỗi khi AI tạo kịch bản tương tác');
    const data = await res.json();
    await refreshCreditBalance('interactive_video');
    return data;
};

// =================================================================================
// USER DATA MANAGEMENT APIs (PRODUCTION)
// =================================================================================

/**
 * Generic fetch function for any resource.
 */
export const fetchResource = async (resource: string): Promise<any[]> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/${resource}`);
        if (!response.ok) return [];
        return await response.json();
    } catch (error) {
        console.error(`Error fetching ${resource}:`, error);
        return [];
    }
};

/**
 * @deprecated Use individual fetchers or fetchResource
 */
export const fetchAllUserData = async (resources?: string): Promise<Record<string, any[]> | null> => {
    if (APP_MODE === 'PRODUCTION') {
        // Purpose: Fetch user-related data (documents, lesson plans, etc.) from the backend.
        // Student code login: dùng endpoint student/data riêng (không user, không credit)
        try {
            const isStudentCode = EDUAPP_AUTH_TYPE === 'student_code';
            const path = isStudentCode ? `${API_EDU_PREFIX}/student/data` : `${API_EDU_PREFIX}/user/data${resources ? `?resources=${resources}` : ''}`;
            const response = await apiFetch(path, {
                method: 'GET',
                headers: {},
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Lỗi không xác định từ máy chủ.' }));
                throw new Error(errorData.message || 'Lỗi từ máy chủ khi tải dữ liệu người dùng.');
            }
            const jsonResponse = await response.json();
            // Hỗ trợ cả trường hợp backend trả về trực tiếp hoặc bọc trong data (phòng lỗi double wrapping)
            const userData = jsonResponse.data ? (jsonResponse.data.data ? jsonResponse.data.data : jsonResponse.data) : jsonResponse;
            
            // Chỉ lưu localStorage khi tải đầy đủ (không phải partial như joinRequests)
            // Tránh ghi đè dữ liệu đầy đủ bằng partial → mất classes khi refresh
            const isFullLoad = !resources || resources === '' || resources === 'all';
            if (userData && CURRENT_USER_ID && isFullLoad) {
                saveUserDataToLocal(CURRENT_USER_ID, userData);
            }
            
            return userData;
        } catch (error) {
            throw handleGeminiError(error, "tải dữ liệu người dùng từ máy chủ");
        }
    }
    // In DEVELOPMENT mode, data is handled by the useLocalStorage hook in App.tsx.
    return Promise.resolve(null);
};

/**
 * Helper để tạo link chia sẻ SEO friendly.
 */
export const getShareLink = (resource: string, id: string | number, title: string, token: string | null): string => {
    const slug = title.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[đĐ]/g, 'd')
        .replace(/([^0-9a-z-\s])/g, '')
        .replace(/(\s+)/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    
    const RESOURCE_URL_MAP: Record<string, string> = {
        'lesson-plans': 'giao-an',
        'drawing-lessons': 'bai-hoc-ve',
        'quizzes': 'de-kiem-tra',
        'games': 'tro-choi',
        'presentations': 'trinh-chieu',
        'scientific-simulations': 'mo-phong-khoa-hoc',
        'homeworks': 'bai-tap',
        'activities': 'hoat-dong',
        'documents': 'tai-lieu',
        'video-projects': 'video-tuong-tac',
        'thiet-ke-3d': 'thiet-ke-3d',
        'ai-fact-check-sessions': 'kiem-chung-ai',
        'ai-ethics-challenge-sessions': 'thu-thach-dao-duc-ai',
        'ai-lab-sessions': 'phong-lab-ai',
    };
    
    const resType = RESOURCE_URL_MAP[resource] || resource;
    const slugWithId = `${slug || 'tai-nguyen'}-i${id}.html`;
    
    let url = `${window.location.origin}/chia-se/${resType}/${slugWithId}`;
    if (token) {
        url += `?token=${token}`;
    }
    return url;
};

/**
 * Cập nhật visibility và link chia sẻ.
 * @param resource - resource type (games, lesson-plans, quizzes, ...)
 * @param id - resource id
 * @param payload - { visibility, shareExpiresAt?, shareScope? }
 */
export const updateResourceVisibility = async (
    resource: string,
    id: string | number,
    payload: { visibility: 'private' | 'link' | 'public' | 'restricted'; shareExpiresAt?: string | null; shareScope?: object }
): Promise<any> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/${encodeURIComponent(resource)}/${encodeURIComponent(id)}/visibility`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Lỗi cập nhật chia sẻ'));
    }
    return response.json();
};

/**
 * Lấy danh sách nhóm tôi sở hữu (cho chia sẻ restricted).
 */
export const fetchGroupsMine = async (): Promise<{ id: number; name: string }[]> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/groups/mine`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.groups || [];
};

/** Tạo nhóm Bookai thêm (khi chưa vượt giới hạn gói / admin). */
export const createGroupBookai = async (name: string): Promise<{ id: number; name: string }> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/group-credits/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error((data as any).message || 'Không thể tạo nhóm');
    }
    return (data as any).group;
};

/**
 * Tìm kiếm người dùng theo email (cho chia sẻ restricted).
 */
export const searchUsersByEmail = async (q: string): Promise<{ id: number; email: string; name?: string }[]> => {
    if (!q || q.length < 2) return [];
    const response = await apiFetch(`${API_EDU_PREFIX}/users/search?q=${encodeURIComponent(q)}`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.users || [];
};

/**
 * Lấy tài nguyên được chia sẻ với tôi (restricted scope).
 */
export const fetchSharedWithMe = async (): Promise<Record<string, any[]>> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/shared/with-me`);
    if (!response.ok) return {};
    return response.json();
};

/**
 * Lấy danh sách tài nguyên công khai từ thư viện cộng đồng.
 * @param type - Lọc theo loại (games, lesson-plans, ...)
 * @param page - Trang phân trang
 * @param limit - Số item mỗi trang
 * @param q - Từ khóa tìm kiếm
 */
export const fetchSharedLibrary = async (params?: {
    type?: string;
    page?: number;
    limit?: number;
    q?: string;
    targetRole?: string;
}): Promise<{ items: any[]; page: number; limit: number; hasMore: boolean; total?: number }> => {
    const sp = new URLSearchParams();
    if (params?.type) sp.set('type', params.type);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.q?.trim()) sp.set('q', params.q.trim());
    if (params?.targetRole) sp.set('targetRole', params.targetRole);
    const qs = sp.toString();
    const url = `${API_BASE_URL}${API_EDU_PREFIX}/shared/library${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Không thể tải thư viện.');
    const data = await response.json();
    return {
        items: data.items || [],
        page: data.page ?? 1,
        limit: data.limit ?? 24,
        hasMore: data.hasMore ?? false,
    };
};

/**
 * Đánh giá tài nguyên (1-5 sao). Cần auth.
 */
export const rateSharedResource = async (
    resource: string,
    id: string | number,
    rating: number
): Promise<{ rating: number; ratingAvg: number; ratingCount: number }> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/shared/${resource}/${id}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating }),
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể đánh giá');
    }
    return response.json();
};

/**
 * Sao chép tài nguyên được chia sẻ vào thư viện của tôi (cần auth).
 */
export const copySharedResourceToMyLibrary = async (
    resource: string,
    id: string | number,
    token?: string | null
): Promise<any> => {
    const url = token
        ? `${API_EDU_PREFIX}/shared/${resource}/${id}/copy?token=${encodeURIComponent(token)}`
        : `${API_EDU_PREFIX}/shared/${resource}/${id}/copy`;
    const response = await apiFetch(url, { method: 'POST' });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể sao chép tài nguyên');
    }
    return response.json();
};

/**
 * Lấy tài nguyên qua link chia sẻ (không cần auth).
 */
export const fetchSharedResource = async (
    resource: string,
    id: string | number,
    token?: string | null
): Promise<any> => {
    const url = token
        ? `${API_BASE_URL}${API_EDU_PREFIX}/shared/${resource}/${id}?token=${encodeURIComponent(token)}`
        : `${API_BASE_URL}${API_EDU_PREFIX}/shared/${resource}/${id}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Lỗi tải tài nguyên'));
    }
    return response.json();
};

/**
 * Lấy chi tiết một tài nguyên theo ID (PRODUCTION)
 */
/**
 * Lấy chi tiết một tài nguyên theo ID (PRODUCTION)
 */
export const fetchResourceById = async (resource: string, id: string | number, forceRefresh: boolean = false): Promise<any | null> => {
    if (APP_MODE !== 'PRODUCTION') return null;
    try {
        // Chỉ thêm timestamp để tránh cache trình duyệt khi thực sự cần thiết (ví dụ: sau khi sửa)
        const url = forceRefresh 
            ? `${API_EDU_PREFIX}/${resource}/${id}?_t=${Date.now()}`
            : `${API_EDU_PREFIX}/${resource}/${id}`;
            
        const response = await apiFetch(url, forceRefresh ? {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
            },
        } : undefined);
        if (!response.ok) return null;
        const result = await response.json();
        // Flatten nested data if present (common pattern in Laravel/other backends)
        return result?.data && (result.data.id || result.data._id) ? { ...result, ...result.data } : result;
    } catch (error) {
        console.error(`Error fetching ${resource} with id ${id}:`, error);
        return null;
    }
};

/**
 * Performs an API sync operation with optimistic update to localStorage and rollback on failure.
 */
export const syncWithRollback = async (
    resource: string, 
    method: 'POST' | 'PATCH' | 'DELETE', 
    data: any, 
    id?: string | number,
    onStateUpdate?: (updatedData: any, action: 'add' | 'update' | 'delete') => void
): Promise<any> => {
    if (APP_MODE !== 'PRODUCTION') return null;
    
    // 1. Prepare for rollback (save current state if needed - usually done via local state management)
    // In this simple implementation, we rely on the caller to provide onStateUpdate for optimistic UI.
    
    const action = method === 'POST' ? 'add' : (method === 'DELETE' ? 'delete' : 'update');
    
    // 2. Optimistic Update Local Storage
    if (CURRENT_USER_ID) {
        updateLocalResource(CURRENT_USER_ID, resource, data, action);
    }
    
    // 3. Optimistic Update React State (via callback)
    if (onStateUpdate) {
        onStateUpdate(data, action);
    }

    // 4. Perform API Sync
    try {
        const response = await syncResource(resource, method, data, id);
        
        if (!response) {
            throw new Error("Sync failed - no response");
        }
        
        // If it was a POST, we might get a real ID back. Update Local Storage and State again.
        if (method === 'POST' && response.id && CURRENT_USER_ID) {
            updateLocalResource(CURRENT_USER_ID, resource, { ...data, ...response }, 'update');
            if (onStateUpdate) onStateUpdate({ ...data, ...response }, 'update');
        }
        
        return response;
    } catch (error) {
        console.error(`Sync error for ${resource} (${method}), rolling back...`, error);
        
        // 5. ROLLBACK
        if (CURRENT_USER_ID) {
            // If it was an add, delete it. If it was a delete/update, we'd need the original data for a full rollback.
            // For simplicity, we'll just log the failure and perhaps show a toast in UI.
            // A full rollback would require passing the previousItem.
        }
        
        throw error; // Rethrow so the UI can handle the error (e.g., show toast)
    }
};

/**
 * Generic sync function for any resource.
 */
export const syncResource = async (resource: string, method: 'POST' | 'PATCH' | 'DELETE', data?: any, id?: string | number): Promise<any> => {
    const url = id ? `${API_EDU_PREFIX}/${resource}/${id}` : `${API_EDU_PREFIX}/${resource}`;
    try {
        const response = await apiFetch(url, {
            method,
            body: data ? JSON.stringify(data) : undefined,
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Sync failed for ${resource} (${method}): Status ${response.status}`, errorText);
            throw new Error(`Failed to sync ${resource}: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error(`Sync error (${resource}):`, error);
        return null;
    }
};

/**
 * Saves all of the user's data to the backend in PRODUCTION mode.
 * @deprecated Use syncResource for individual updates.
 */
export const saveAllUserData = async (data: Record<string, any[]>): Promise<boolean> => {
    if (APP_MODE === 'PRODUCTION') {
        try {
            const response = await apiFetch(`${API_EDU_PREFIX}/user/data`, {
                method: 'POST',
                body: JSON.stringify({ data }),
            });
            if (response.ok) {
            } else {
                console.error("Save User Data Failed:", response.status);
            }
            return response.ok;
        } catch (error) {
            console.error("Failed to save user data to backend:", error);
            return false;
        }
    }
    return true;
};

// ==========================================
// User Settings (PRODUCTION)
// ==========================================
export const getUserSettings = async (): Promise<any> => {
    if (APP_MODE !== 'PRODUCTION') return Promise.resolve(null);
    const res = await apiFetch(`${API_EDU_PREFIX}/user/settings`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được user settings');
    const data = await res.json();
    return data.settings;
};

export const updateUserSettings = async (settings: any): Promise<boolean> => {
    if (APP_MODE !== 'PRODUCTION') return Promise.resolve(true);
    const res = await apiFetch(`${API_EDU_PREFIX}/user/settings`, {
        method: 'PATCH',
        body: JSON.stringify({ settings })
    });
    if (!res.ok) throw new Error('Không cập nhật được user settings');
    return true;
};

/** ID thông báo có thể cập nhật/xóa (từ edu_notifications). Announcements (ann_*) không lưu DB. */
const isEditableNotificationId = (id: string): boolean => /^\d+$/.test(String(id));

/** Đánh dấu một thông báo đã đọc (lưu DB). */
export const markNotificationAsRead = async (id: string): Promise<void> => {
    if (APP_MODE !== 'PRODUCTION' || !isEditableNotificationId(id)) return;
    const res = await apiFetch(`${API_EDU_PREFIX}/notifications/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isRead: true })
    });
    if (!res.ok) throw new Error('Không thể đánh dấu đã đọc');
};

/** Đánh dấu tất cả thông báo đã đọc (lưu DB). */
export const markAllNotificationsAsRead = async (notificationIds: string[]): Promise<void> => {
    if (APP_MODE !== 'PRODUCTION') return;
    const editableIds = notificationIds.filter(isEditableNotificationId);
    await Promise.all(editableIds.map(id => apiFetch(`${API_EDU_PREFIX}/notifications/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isRead: true })
    })));
};

/** Xóa các thông báo đã đọc khỏi DB. */
export const deleteReadNotifications = async (readNotificationIds: string[]): Promise<void> => {
    if (APP_MODE !== 'PRODUCTION') return;
    const editableIds = readNotificationIds.filter(isEditableNotificationId);
    await Promise.all(editableIds.map(id => apiFetch(`${API_EDU_PREFIX}/notifications/${id}`, { method: 'DELETE' })));
};

/** Upload avatar cho user. Trả về URL avatar đã format. */
export const uploadUserAvatar = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await fetch(apiUrl(`${API_EDU_PREFIX}/user/avatar`), {
        method: 'POST',
        headers: { ...authHeader(), 'Accept': 'application/json' },
        body: formData,
        credentials: 'include'
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as any)?.message || 'Không thể tải avatar lên.');
    }
    const data = await response.json();
    const url = (data as any)?.avatar;
    return url ? (formatAssetUrl(url) || url) : '';
};

/** Phụ huynh: Liên kết với tài khoản học sinh (nhập email con) */
export const linkStudentAccount = async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/user/link-student`, {
            method: 'POST',
            body: JSON.stringify({ email: email.trim() })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { success: false, message: (data as any)?.message || 'Không thể liên kết.' };
        return { success: true, message: (data as any)?.message || 'Đã liên kết thành công.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'Lỗi kết nối.' };
    }
};

/** Học sinh: Tìm phụ huynh theo email để liên kết */
export const searchParentForLink = async (email: string): Promise<{ success: boolean; children: { child_id: number; child_name: string; student_id: number; student_name: string }[]; message?: string }> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/user/link-parent-search?email=${encodeURIComponent(email.trim())}`, { method: 'GET' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { success: false, children: [], message: (data as any)?.message };
        return { success: true, children: (data as any)?.children || [], message: (data as any)?.message };
    } catch (e: any) {
        return { success: false, children: [], message: e?.message || 'Lỗi kết nối.' };
    }
};

/** Học sinh: Xác nhận liên kết với hồ sơ học sinh */
export const linkParentConfirm = async (studentId: number): Promise<{ success: boolean; message: string }> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/user/link-parent-confirm`, {
            method: 'POST',
            body: JSON.stringify({ student_id: studentId })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { success: false, message: (data as any)?.message || 'Không thể liên kết.' };
        return { success: true, message: (data as any)?.message || 'Đã liên kết thành công.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'Lỗi kết nối.' };
    }
};

// ==========================================
// Ads Config (PRODUCTION)
// ==========================================
export const getAdsConfig = async (): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/ads/config`, { method: 'GET' });
    if (!res.ok) throw new Error('Không tải được cấu hình quảng cáo');
    return await res.json();
};

/** Lấy danh sách banner popup (public - cả khi chưa đăng nhập) */
export const getBanners = async (): Promise<Banner[]> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/banners`, { method: 'GET' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
};

/** Lấy thông báo hệ thống (public - cả khi chưa đăng nhập) */
export const getAnnouncements = async (): Promise<any[]> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/announcements`, { method: 'GET' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
};

// ==========================================
// Credits (PRODUCTION)
// ==========================================
export const getCreditBalance = async (): Promise<number> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/balance`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được số dư credit');
    const data = await res.json();
    return data.balance as number;
};

export const getCreditPackages = async (): Promise<any[]> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/packages`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được gói credit');
    const data = await res.json();
    return data.packages as any[];
};

/**
 * Lay packages kem `careerJourneyBaseline` (admin free quota baseline).
 *
 * Career Journey: pkg.max_children/max_saved_careers la DELTA so voi free baseline.
 *  - Effective max children = baseline.maxChildren + pkg.max_children
 *  - Effective max saved careers = baseline.maxSavedCareers + pkg.max_saved_careers
 *
 * Dung trong UI Cua hang Credit de hien thi quota hieu luc thay vi raw delta.
 */
export const getCreditPackagesWithBaseline = async (): Promise<{
    packages: any[];
    careerJourneyBaseline: { maxChildren: number; maxSavedCareers: number };
}> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/packages`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được gói credit');
    const data = await res.json();
    return {
        packages: (data.packages || []) as any[],
        careerJourneyBaseline: {
            maxChildren: Number(data?.careerJourneyBaseline?.maxChildren ?? 1),
            maxSavedCareers: Number(data?.careerJourneyBaseline?.maxSavedCareers ?? 3),
        },
    };
};

export const previewCreditCoupon = async (packageId: string | number, couponCode: string): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/coupons/preview`, {
        method: 'POST',
        body: JSON.stringify({ packageId, couponCode }),
    });
    if (!res.ok) throw new Error(await readErrorMessage(res, 'Không áp dụng được mã ưu đãi'));
    return await res.json();
};

export const purchaseCreditPackage = async (packageId: number, provider?: string, couponCode?: string): Promise<{ orderId: number; status: string; }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/purchase`, {
        method: 'POST',
        body: JSON.stringify({ packageId, provider, couponCode }),
    });
    if (!res.ok) throw new Error('Không thể mua gói credit');
    return await res.json();
};

export const getCreditHistory = async (page: number = 1, limit: number = 20): Promise<{ data: any[], total: number, last_page: number, current_page: number }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/history?page=${page}&limit=${limit}`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được lịch sử credit');
    return await res.json();
};

export const getCreditOrders = async (page: number = 1, limit: number = 20): Promise<{ data: any[], total: number, last_page: number, current_page: number }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/orders?page=${page}&limit=${limit}`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được danh sách đơn hàng');
    return await res.json();
};

export const getOrderDetails = async (orderId: number): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/order/${orderId}`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được thông tin đơn hàng');
    return await res.json();
};

/** Xác nhận thanh toán PayOS từ redirect (khi user quay về với status=PAID) */
export const confirmPayosRedirect = async (params: { orderCode: number; status?: string; code?: string; cancel?: string }): Promise<{ success: boolean; credits?: number; message?: string; alreadyProcessed?: boolean; error_code?: string }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/confirm-payos-redirect`, {
        method: 'POST',
        body: JSON.stringify(params),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.message || 'Không xác nhận được thanh toán') as Error & { status?: number; errorCode?: string };
        err.status = res.status;
        err.errorCode = data.error_code;
        throw err;
    }
    return data;
};

/** Gửi bằng chứng chuyển khoản thủ công (Bank/MoMo) */
export const confirmManualPayment = async (
    orderId: number,
    proofImage?: File | null,
    proofNote?: string
): Promise<{
    success: boolean;
    message: string;
    order?: {
        id: number;
        proof_image?: string | null;
        proof_note?: string | null;
    };
}> => {
    const formData = new FormData();
    formData.append('orderId', String(orderId));
    if (proofImage) {
        formData.append('proofImage', proofImage);
    }
    if (proofNote) formData.append('proofNote', proofNote);

    const authData = localStorage.getItem('EDUAPP_AUTH');
    const token = authData ? JSON.parse(authData).accessToken : null;

    const res = await fetch(apiUrl(`${API_EDU_PREFIX}/credits/confirm-manual`), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData,
    });
    
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Không gửi được bằng chứng');
    return data;
};

export const submitFeedback = async (rating: number, comment: string, feature?: string): Promise<{ success: boolean, message: string }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ rating, comment, feature, url: window.location.href })
    });
    if (!res.ok) {
        if (res.status === 429) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || 'Bạn đang gửi góp ý quá nhanh. Vui lòng thử lại sau ít phút.');
        }
        throw new Error('Không gửi được góp ý. Vui lòng thử lại sau.');
    }
    return await res.json();
};

// ==========================================
// Group Credits (PRODUCTION)
// ==========================================
export const getGroupCreditsSummary = async (): Promise<{ group: any; members: any[]; usedThisMonth: number; policy?: any; monthlyPool?: number; needsConfirmation?: boolean; cycle?: { start: string; end: string } }> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve({ group: null as any, members: [], usedThisMonth: 0 });
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/summary`, { method: 'GET' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).message || 'Không tải được tổng quan nhóm');
  return data;
};

export const addGroupMember = async (memberEmail: string, monthlyQuota = 0): Promise<boolean> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve(true);
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/members`, {
    method: 'POST',
    body: JSON.stringify({ member_email: memberEmail, monthly_quota: monthlyQuota })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).message || 'Không thêm được thành viên');
  return true;
};

export const removeGroupMember = async (memberEmail: string): Promise<boolean> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve(true);
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/members`, {
    method: 'DELETE',
    body: JSON.stringify({ member_email: memberEmail })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).message || 'Không xóa được thành viên');
  return true;
};

export const shareGroupCredits = async (toEmail: string, amount: number): Promise<boolean> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve(true);
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/share`, {
    method: 'POST',
    body: JSON.stringify({ to_email: toEmail, amount })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Không chia sẻ được credit');
  }
  return true;
};

export const confirmGroupMembers = async (): Promise<{ ok: boolean; result: any }> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve({ ok: true, result: {} });
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/confirm-members`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).message || 'Không thể xác nhận thành viên');
  return data;
};

// ==========================================
// Social Login (PRODUCTION) — gọi API trong Module EduApp: /api/edu/auth/*
// ==========================================
/** Đăng nhập nhanh học sinh: mã 8 ký tự (A-Z,0-9) + ngày/tháng sinh (DDMM). Có thể cần security_answer nếu lớp có câu hỏi bảo mật. */
export const studentQuickLogin = async (code: string, dobDigits: string, securityAnswer?: string): Promise<{ student: { id: number; name: string; email: string; studentId?: string; loginCode: string; dob?: string }; access_token: string }> => {
    const body: Record<string, string> = { code: code.toUpperCase(), dobDigits };
    if (securityAnswer !== undefined && securityAnswer !== '') body.security_answer = securityAnswer;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/student-login`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = (err as { message?: string }).message;
        if (res.status === 429) throw new Error(msg || 'Tài khoản tạm khóa. Vui lòng thử lại sau 15 phút.');
        if (res.status === 422 && (err as { requires_security_answer?: boolean }).requires_security_answer) {
            const e = new Error(msg || 'Lớp học yêu cầu trả lời câu hỏi bảo mật.') as Error & { requiresSecurityAnswer?: boolean; securityQuestion?: string };
            e.requiresSecurityAnswer = true;
            e.securityQuestion = (err as { security_question?: string }).security_question || '';
            throw e;
        }
        throw new Error(msg || 'Mã đăng nhập không chính xác hoặc đã hết hạn.');
    }
    const data = await res.json();
    const s = data.student;
    const token = data.access_token;
    if (!s || !token) throw new Error('Phản hồi không hợp lệ.');
    return {
        access_token: token,
        student: {
            id: s.id,
            name: s.name,
            email: s.email || `student_${code}@quick.login`,
            studentId: s.studentId,
            loginCode: s.loginCode,
            dob: s.dob,
        },
    };
};

/** Đăng nhập bằng email/password. Gọi API EduApp. */
export const login = async (username: string, password: string): Promise<string> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/login`, {
        method: 'POST',
        body: JSON.stringify({ email: username, password }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin.');
    }
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

const REFERRAL_STORAGE_KEY = 'EDUAPP_REFERRAL_CODE';

/** Lấy mã giới thiệu từ URL (?ref=CODE) và lưu vào localStorage. Gọi khi load trang đăng nhập. */
export const captureReferralFromUrl = (): string | null => {
    try {
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref') || params.get('referral');
        if (ref && ref.length >= 4) {
            localStorage.setItem(REFERRAL_STORAGE_KEY, ref.trim());
            return ref.trim();
        }
        return localStorage.getItem(REFERRAL_STORAGE_KEY);
    } catch { return null; }
};

/** Xóa mã giới thiệu đã lưu (sau khi đăng ký thành công). */
export const clearReferralCode = () => { try { localStorage.removeItem(REFERRAL_STORAGE_KEY); } catch {} };

/** Lấy mã giới thiệu từ URL hoặc localStorage. */
export const getReferralCode = (): string | null => {
    captureReferralFromUrl();
    return localStorage.getItem(REFERRAL_STORAGE_KEY);
};

/** Đăng nhập bằng Google id_token (từ GSI / One Tap). Gọi API EduApp. */
export const loginWithGoogleIdToken = async (idToken: string, referralCode?: string | null): Promise<string> => {
    const body: Record<string, unknown> = { google_id_token: idToken };
    if (referralCode) body.referral_code = referralCode;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/google-login`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Đăng nhập Google thất bại.');
    }
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

/** Đăng nhập bằng Google access_token (từ Token Client với scope birthday). Lấy được DOB từ People API. */
export const loginWithGoogleAccessToken = async (accessToken: string, referralCode?: string | null): Promise<string> => {
    const body: Record<string, unknown> = { google_access_token: accessToken };
    if (referralCode) body.referral_code = referralCode;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/google-login`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Đăng nhập Google thất bại.');
    }
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

export const loginWithGoogle = async (googleId: string, googleAccessToken: string, referralCode?: string | null): Promise<string> => {
    const body: Record<string, unknown> = { google_id: googleId, google_token: googleAccessToken };
    if (referralCode) body.referral_code = referralCode;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/google-login`, {
        method: 'POST',
        headers: {},
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Google login failed');
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

/** Đăng nhập bằng Facebook. Gọi API EduApp. */
export const loginWithFacebook = async (facebookId: string, facebookAccessToken: string, referralCode?: string | null): Promise<string> => {
    const body: Record<string, unknown> = { facebook_id: facebookId, facebook_token: facebookAccessToken };
    if (referralCode) body.referral_code = referralCode;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/facebook-login`, {
        method: 'POST',
        headers: {},
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Facebook login failed');
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

// ==========================================
// Class Management (PRODUCTION)
// ==========================================
type ClassDto = { 
    id: string|number; 
    name: string; 
    academic_year?: string|null; 
    grade_level?: string|null; 
    class_type: 'physical'|'virtual'|'hybrid'; 
    enrollment_mode?: 'manual'|'open'|'invite_only'|'student_code';
    location?: string;
    invite_code: string;
    invite_expiry_date?: string|null;
    security_question?: string|null;
    is_locked?: boolean;
    teacher_name?: string;
};
type StudentDto = { id: string|number; name: string; student_id_code?: string|null; dob?: string|null };
type AssignmentDto = { id: number; homework_id: number; class_id: number; assigned_date: string; due_date: string|null };

export const findClassByCode = async (code: string): Promise<ClassDto | null> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve(null);
  const codeStr = (typeof code === 'string' ? code : '').replace(/\s/g, '').trim();
  if (!codeStr || codeStr.includes('[object')) return Promise.resolve(null);
  try {
    const res = await apiFetch(`${API_EDU_PREFIX}/class/find/${encodeURIComponent(codeStr)}`, { method: 'GET' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.class as ClassDto;
  } catch {
    return null;
  }
};

export const getClasses = async (): Promise<ClassDto[]> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve([]);
  const res = await apiFetch(`${API_EDU_PREFIX}/class`, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được danh sách lớp');
  const data = await res.json();
  return data.classes as ClassDto[];
};

export const createClass = async (payload: { 
    name: string; 
    academic_year?: string; 
    grade_level?: string; 
    class_type: 'physical'|'virtual'|'hybrid';
    enrollment_mode?: 'manual'|'open'|'invite_only';
    allow_join_from_outside?: boolean;
    location?: string;
    invite_expiry_date?: string|null;
    security_question?: string|null;
    security_answer?: string|null;
    is_locked?: boolean;
}): Promise<ClassDto> => {
  try {
    const res = await apiFetch(`${API_EDU_PREFIX}/class`, { method: 'POST', body: JSON.stringify(payload) });
    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Lỗi máy chủ (${res.status}): Không tạo được lớp`);
    }
    const data = await res.json();
    if (!data.class) throw new Error('Phản hồi từ máy chủ không hợp lệ (thiếu dữ liệu lớp)');
    return data.class as ClassDto;
  } catch (error: any) {
    console.error("createClass API Error:", error);
    throw error;
  }
};

export const updateClassInfo = async (id: string|number, payload: Partial<{ 
    name: string; 
    academic_year: string|null; 
    grade_level: string|null; 
    class_type: 'physical'|'virtual'|'hybrid';
    enrollment_mode: 'manual'|'open'|'invite_only';
    allow_join_from_outside: boolean;
    location: string|null;
    invite_expiry_date: string|null;
    security_question: string|null;
    security_answer: string|null;
    is_locked: boolean;
}>): Promise<boolean> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  if (!res.ok) throw new Error('Không cập nhật được lớp');
  return true;
};

export const deleteClassById = async (id: string|number): Promise<boolean> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Không xóa được lớp');
  return true;
};

export const getClassStudents = async (classId: string|number, inviteCode?: string): Promise<StudentDto[]> => {
  const url = inviteCode ? `${API_EDU_PREFIX}/class/${classId}/students?invite_code=${inviteCode}` : `${API_EDU_PREFIX}/class/${classId}/students`;
  const res = await apiFetch(url, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được học sinh của lớp');
  const data = await res.json();
  return data.students as StudentDto[];
};

export const addStudentToClassApi = async (classId: string|number, payload: { student_id?: number; name?: string; real_name?: string; dob?: string; student_id_code?: string; parents?: any[] }): Promise<{ added: boolean; student: StudentDto }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students`, { method: 'POST', body: JSON.stringify(payload) });
  if (!res.ok) throw new Error('Không thêm được học sinh');
  return await res.json();
};

export const addMultipleStudentsApi = async (classId: string|number, studentData: {
    name: string;
    student_id_code?: string;
    dob?: string;
    parent_name?: string;
    parent_phone?: string;
    parent_relationship?: string;
    parent_name_2?: string;
    parent_phone_2?: string;
    parent_relationship_2?: string;
}[]): Promise<{ added: number; students: StudentDto[] }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/bulk`, { 
    method: 'POST', 
    body: JSON.stringify({ students: studentData }) 
  });
  if (!res.ok) throw new Error('Không thêm được danh sách học sinh');
  return await res.json();
};

export const removeStudentFromClassApi = async (classId: string|number, studentId: string|number): Promise<boolean> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/${studentId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Không xóa học sinh khỏi lớp');
  return true;
};

export const analyzePronunciationApi = async (audioBase64: string, phrase: string, mimeType: string = 'audio/webm'): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/pronunciation-analysis`, {
        method: 'POST',
        body: JSON.stringify({ audio: audioBase64, phrase, mimeType })
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || 'Lỗi phân tích phát âm');
    }
    const data = await res.json();
    await refreshCreditBalance('pronunciation-analysis');
    return data;
};

export const textToSpeechApi = async (text: string, voice?: string): Promise<{ url: string; size: number; path: string }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/text-to-speech`, {
        method: 'POST',
        body: JSON.stringify({ text, voice })
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || 'Lỗi chuyển văn bản thành giọng nói');
    }
    const data = await res.json();
    await refreshCreditBalance('text-to-speech');
    return data;
};

export const regenerateStudentLoginCodeApi = async (classId: string|number, studentId: string|number): Promise<{ login_code: string; student: StudentDto }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/${studentId}/regenerate-login-code`, { method: 'POST' });
  if (!res.ok) throw new Error('Không thể sinh mã mới');
  return await res.json();
};

/**
 * --- Oral Quiz APIs ---
 */
export const generateOralQuiz = async (
    gradeLevel: GradeLevel,
    subject: string,
    topic: string,
    detailedInstruction: string,
    contextContent: string | null,
    allowAiAugmentation: boolean,
    studentCount: number,
    questionsPerStudent: number,
    questionTypes: string[] = ['ShortAnswer'],
    questionTypeWeights: Record<string, number> = {},
    aiProvider: AIProvider = 'gemini',
    id?: string | number
): Promise<any> => {
    const payload = { gradeLevel, subject, topic, detailedInstruction, contextContent, allowAiAugmentation, studentCount, questionsPerStudent, questionTypes, questionTypeWeights, aiProvider, id };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/oral-quiz`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo nội dung kiểm tra kiến thức.');
        const data = await response.json();
        await refreshCreditBalance('oral-quiz');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo nội dung kiểm tra kiến thức");
    }
};

export const saveOralQuiz = async (quiz: any): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/oral-quizzes`, {
        method: 'POST',
        body: JSON.stringify(quiz)
    });
    if (!res.ok) throw new Error('Không lưu được nội dung kiểm tra kiến thức');
    const data = await res.json();
    return data.quiz || data;
};

export const generateAiEthicsScenariosApi = async (topic: string, content: string = ''): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/ai-ethics-scenarios`, {
        method: 'POST',
        body: JSON.stringify({ topic, content })
    });
    if (!res.ok) throw new Error('Không tạo được thử thách đạo đức AI');
    return await res.json();
};

export const generateAiFactCheckAnalysisApi = async (claim: string, content: string = ''): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/ai-fact-check`, {
        method: 'POST',
        body: JSON.stringify({ claim, content })
    });
    if (!res.ok) throw new Error('Không thực hiện được kiểm chứng AI');
    return await res.json();
};

export const startOralQuizSession = async (oralQuizId: string | number, classId: string | number, selectedStudentIds: string[]): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/oral-quiz-sessions/start`, {
        method: 'POST',
        body: JSON.stringify({ oral_quiz_id: oralQuizId, class_id: classId, selected_student_ids: selectedStudentIds })
    });
    if (!res.ok) throw new Error('Không khởi tạo được phiên kiểm tra');
    return await res.json();
};

export const updateOralQuizSession = async (sessionId: string | number, data: any): Promise<any> => {
    // Map camelCase to snake_case for backend
    const payload: Record<string, any> = {};
    if (data.currentStudentId !== undefined) payload.current_student_id = data.currentStudentId;
    if (data.results !== undefined) payload.results = data.results;
    if (data.status !== undefined) payload.status = data.status;

    const res = await apiFetch(`${API_EDU_PREFIX}/oral-quiz-sessions/${sessionId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('Không cập nhật được phiên kiểm tra');
    return await res.json();
};

export const listAssignmentsByClass = async (classId: string|number): Promise<AssignmentDto[]> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/assignments`, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được danh sách giao bài');
  const data = await res.json();
  return data.assignments as AssignmentDto[];
};

export const assignHomeworkToClass = async (classId: string|number, homeworkId: number, dueDate: string): Promise<AssignmentDto> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/assignments`, { method: 'POST', body: JSON.stringify({ homework_id: homeworkId, due_date: dueDate, type: 'homework' }) });
  if (!res.ok) throw new Error('Không giao bài tập');
  const data = await res.json();
  return data.assignment as AssignmentDto;
};

export type CreateAssignmentPayload = {
  type: 'homework' | 'task' | 'announcement';
  homework_id?: number | null;
  title?: string;
  due_date?: string;
  instructions?: string;
  student_ids?: string[];
  scheduled_date?: string;
  passing_grade?: number;
  is_required?: boolean;
};

export const createAssignment = async (classId: string|number, payload: CreateAssignmentPayload): Promise<{ id: number; [k: string]: any }> => {
  const body: Record<string, any> = {
    type: payload.type,
    title: payload.title,
    due_date: payload.due_date,
    instructions: payload.instructions,
    student_ids: payload.student_ids,
    scheduled_date: payload.scheduled_date,
    passing_grade: payload.passing_grade,
    is_required: payload.is_required,
  };
  if (payload.type === 'homework' && payload.homework_id) {
    body.homework_id = payload.homework_id;
  } else if (payload.homework_id) {
    body.homework_id = payload.homework_id;
  }
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/assignments`, { method: 'POST', body: JSON.stringify(body) });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || 'Không giao bài tập');
  }
  const data = await res.json();
  return data.assignment;
};

export const getVideoDownloadUrl = (video: VideoResource): string => {
  if (APP_MODE !== 'PRODUCTION' || !video.id || video.id.toString().startsWith('id_')) {
    return video.uri;
  }
  // Trả về path tương đối để apiFetch có thể xử lý (thêm base url và auth token)
  return `${API_EDU_PREFIX}/video-resources/${video.id}/download`;
};

/**
 * Generic API Service object for general requests.
 */
export const apiService = {
    get: async (path: string) => {
        const res = await apiFetch(path, { method: 'GET' });
        if (!res.ok) {
            // Đọc message từ body để FE có thể hiển thị toast thân thiện thay vì status code khô khan
            const err = await res.json().catch(() => ({ error: 'Request failed' }));
            throw new Error(err.error || err.message || `GET ${path} failed: ${res.status}`);
        }
        return res.json();
    },
    post: async (path: string, body: any) => {
        const res = await apiFetch(path, {
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Request failed' }));
            throw new Error(err.error || err.message || `POST ${path} failed: ${res.status}`);
        }
        return res.json();
    },
    patch: async (path: string, body: any) => {
        const res = await apiFetch(path, {
            method: 'PATCH',
            body: body ? JSON.stringify(body) : undefined
        });
        if (!res.ok) throw new Error(`PATCH ${path} failed: ${res.status}`);
        return res.json();
    },
    delete: async (path: string) => {
        const res = await apiFetch(path, { method: 'DELETE' });
        if (!res.ok) throw new Error(`DELETE ${path} failed: ${res.status}`);
        return res.json();
    }
};
